//src/app/features/documents/index.tsx
import { useSelectItems } from 'app/hooks/useSelectItems';
import { DashboardContainerHeader } from 'app/layout/dashboardLayout/BoxLayout';
import { IGetQueryParams } from 'app/services';
import { useLazyGetGuestDirectoryItemsQuery } from 'app/services/constants';
import {
  IDirectoryNode,
  useLazyGetDirectoryItemsQuery,
} from 'app/services/documents';
import { IProject } from 'app/services/responseTypes';
import { ITask } from 'app/services/tasks';
import { IUser } from 'app/services/user';
import { DeleteDocumentIcon, UploadWhiteIcon } from 'assets/icons';
import Button from 'Components/Buttons/Button';
import CheckboxInput from 'Components/Form/Inputs/CheckboxInput';
import { Grid } from 'Components/Grid';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import usePagination from 'Components/Pagination/usePagination';
import { useCurrentPopup } from 'Components/Popups';
import Popup from 'Components/Popups/Popup';
import { PopupHandles } from 'Components/Popups/Popup/type';
import Spinner from 'Components/Spinner';
import { createContext, ReactNode, useContext, useRef } from 'react';

import { useAuth } from 'utils/AuthProvider';
import ActionsPopup from './ActionsPopup';
import CreateNodesForm from './CreateNodesForm';
import { DirectoryNodeDetailsModal } from './DirectoryNodeDetails';
import { GridDocumentRow } from './DocumentRow';
import DocumentsFilters, { DocumentsFiltersType } from './DocumentsFilters';
import MoveModal from './MoveModal';
import MultiNodeActions from './MultiNodeActions';

import ShareModal from './ShareModal';
import { RestoreModal } from './RestoreModal';

export type DocumentsProps = {
  lockClient?: IUser;
  lockProject?: IProject;
  lockTask?: ITask;
  lockParentId?: number;
};

export function useGridSelect(
  nodes?: IDirectoryNode[],
  canSelect: (d: IDirectoryNode) => boolean = () => true,
) {
  return useSelectItems(nodes, { canSelect });
}
type GridSelectType = ReturnType<typeof useGridSelect>;
type DocumentsDataType = ReturnType<typeof useDocumentsData>;
type DocumentModalsType = ReturnType<typeof useDocumentModalRefs>['Modals'];
type DocumentsContextType = DocumentsDataType & DocumentsProps & GridSelectType;
export const DocumentsContext = createContext<
  DocumentsContextType & DocumentModalsType
>({} as any);
export function useDocumentsData(
  props: DocumentsProps,
  initialParams: IGetQueryParams<DocumentsFiltersType>,
  skipQueryParamsCache = false,
) {
  const { user: loggedInUser, isClient, isAuthenticated } = useAuth();
  const lockClient = isClient ? loggedInUser : props.lockClient;

  const data = usePagination(
    isAuthenticated
      ? useLazyGetDirectoryItemsQuery
      : useLazyGetGuestDirectoryItemsQuery,
    initialParams,
    {
      skipQueryParamsCache,
    },
  );
  const directory =
    data.response?.file_object ||
    ({
      documents_count: 0,
      subfolders_count: 0,
    } as IDirectoryNode);
  const canGoBack =
    !props.lockParentId ||
    (directory?.parent_id && directory?.id !== props.lockParentId);
  return {
    ...data,
    ...props,
    canGoBack,
    lockClient,
    directory,
  };
}
export const useDocumentsContext = () => useContext(DocumentsContext);

export default function Documents(props: DocumentsProps) {
  const data = useDocumentsData(props, {
    filters: {
      // initial value
      is_trashed: false,
      client_id: props.lockClient?.id,
      task_id: props.lockTask?.id,
      project_id: props.lockProject?.id,
      id: props.lockParentId,
    },
    per_page: 20,
  });

  const GridSelect = useGridSelect(data.data);

  if (!data.response) return <Spinner />;
  return (
    <DocumentsContextProvider
      {...{
        ...data,
        ...GridSelect,
      }}
    >
      <DocumentsHeader />
      <DocumentsGrid />
    </DocumentsContextProvider>
  );
}

function DocumentsGrid() {
  const { isClient } = useAuth();
  const {
    directory,
    filters,
    canGoBack,
    isLoading,
    Pagination,
    data,
    isAllSelected,
    selectUnselectPage,
    selectableItems,
    isFetching,
  } = useDocumentsContext();
  const columns = [
    {
      render: (
        <CheckboxInput
          checked={isAllSelected}
          disabled={selectableItems.length === 0}
          onChange={selectUnselectPage}
        />
      ),
      width: '45px',
      mobile: true,
    },
    { render: 'Name', mobile: true },
    'Client',
    'Business',
    'Tax / Task',
    { render: 'Actions', width: '75px', mobile: true },
  ];
  if (isClient) {
    // remove client column
    columns.splice(2, 1);
  }
  return (
    <Grid
      columns={columns}
      headerClassName="bg-gray-700 text-white"
      data={data as IDirectoryNode[]}
      isLoading={isFetching}
      gridRowWrap={false}
      footer={<Pagination />}
      firstRow={
        filters.id &&
        canGoBack &&
        directory && <GridDocumentRow rowType="Back" node={directory} />
      }
    >
      {(node) => <GridDocumentRow node={node} />}
    </Grid>
  );
}
function DocumentsHeader() {
  const { isAuthenticated } = useAuth();
  const { directory, filters, setFilters, selectedCount } =
    useDocumentsContext();
  return (
    <>
      <DashboardContainerHeader
        title={filters?.is_trashed ? 'Recycle Bin' : 'Documents'}
        subTitle={
          directory && (
            <div className="flex items-center gap-2 h-5">
              <div className="flex gap-1 capitalize text-sm text-gray-light whitespace-nowrap">
                <span>{directory?.subfolders_count} folders</span>
                <span>{directory?.documents_count} files</span>
              </div>
            </div>
          )
        }
      >
        {isAuthenticated && (
          <>
            {selectedCount > 0 && <MultiNodeActions />}
            <DocumentsFilters
              directory={directory}
              value={filters}
              onChange={(newFiltersValues) =>
                setFilters({ ...filters, ...newFiltersValues })
              }
            />
            <div className="flex flex-1 xl:flex-grow-0 gap-2 items-center">
              <CreateDocumentButton />
              <ViewTrashButton /> {/* Updated button */}
            </div>
          </>
        )}
      </DashboardContainerHeader>
    </>
  );
}
function useDocumentModalRefs() {
  const ActionsPopupRef = useRef<PopupHandles<IDirectoryNode>>(null);
  const FolderDetailsModalRef = useRef<PopupHandles<IDirectoryNode>>(null);
  const DocumentDetailsModalRef = useRef<PopupHandles<IDirectoryNode>>(null);
  const ShareModalRef = useRef<PopupHandles<IDirectoryNode>>(null);
  const MoveModalRef = useRef<PopupHandles<IDirectoryNode[]>>(null);
  const RestoreModalRef = useRef<PopupHandles<IDirectoryNode[]>>(null);
  const CreateNodeModalRef =
    useRef<PopupHandles<{ parent: IDirectoryNode }>>(null);
  return {
    Modals: {
      ShareModal: ShareModalRef.current!,
      MoveModal: MoveModalRef.current!,
      FolderDetailsModal: FolderDetailsModalRef.current!,
      CreateNodeModal: CreateNodeModalRef.current!,
      DocumentDetailsModal: DocumentDetailsModalRef.current!,
      ActionsPopupRef: ActionsPopupRef.current!,
      RestoreModal: RestoreModalRef.current!,
    },
    Refs: {
      ActionsPopupRef,
      FolderDetailsModalRef,
      DocumentDetailsModalRef,
      RestoreModalRef,
      CreateNodeModalRef,
      ShareModalRef,
      MoveModalRef,
    },
  };
}
function DocumentsModals({
  refs,
}: {
  refs: ReturnType<typeof useDocumentModalRefs>['Refs'];
}) {
  const { unselectAll } = useDocumentsContext();
  return (
    <>
      <Popup ref={refs.ActionsPopupRef}>
        <ActionsPopup />
      </Popup>
      <Popup type="modal" ref={refs.CreateNodeModalRef}>
        <CreateNodeModalWindow />
      </Popup>
      <Popup type="modal" ref={refs.FolderDetailsModalRef}>
        <DirectoryNodeDetailsModal />
      </Popup>
      <Popup type="modal" ref={refs.DocumentDetailsModalRef}>
        <DirectoryNodeDetailsModal />
      </Popup>
      <Popup type="modal" ref={refs.ShareModalRef}>
        <ShareModal />
      </Popup>
      <Popup
        type="modal"
        ref={refs.MoveModalRef}
        hooks={{
          onClose: unselectAll,
        }}
      >
        <MoveModal />
      </Popup>
      <Popup
        type="modal"
        ref={refs.RestoreModalRef}
        hooks={{
          onClose: unselectAll,
        }}
      >
        <RestoreModal />
      </Popup>
    </>
  );
}
export function DocumentsContextProvider({
  children,
  ...rest
}: {
  children: ReactNode;
} & DocumentsContextType) {
  const { Modals, Refs } = useDocumentModalRefs();
  return (
    <DocumentsContext.Provider
      value={{
        ...Modals,
        ...rest,
      }}
    >
      {children}
      <DocumentsModals refs={Refs} />
    </DocumentsContext.Provider>
  );
}
function CreateNodeModalWindow() {
  const { close: closeModal, contextData } = useCurrentPopup<{
    parent: IDirectoryNode;
  }>();
  if (!contextData) return <></>;
  return (
    <OverlayWindow
      size={WindowSize.xl + ' min-h-[40rem]'}
      onClose={closeModal}
      paddingSize={'px-0 py-0'}
    >
      <CreateNodesForm />
    </OverlayWindow>
  );
}
export function omit<T>(key: keyof T, obj: T) {
  const { [key]: ommitted, ...rest } = obj;
  return rest;
}
export function add<T, D>(item: D, obj: T) {
  return {
    ...obj,
    ...item,
  };
}
function CreateDocumentButton() {
  const { CreateNodeModal, directory, filters } = useDocumentsContext();
  return (
    <Button
      onClick={() =>
        CreateNodeModal?.openWithContext({
          data: { parent: directory },
        })
      }
      disabled={filters.is_trashed}
    >
      <UploadWhiteIcon className="w-5 h-5" />
      <span>Add New</span>
    </Button>
  );
}
function ViewTrashButton() {
  const { unselectAll, filters, setFilters } = useDocumentsContext();
  return (
    <Button
      theme={filters.is_trashed ? 'green-outline' : 'gray-outline-mono'}
      onClick={() => {
        setFilters({
          ...filters,
          id: undefined,
          is_trashed: !filters.is_trashed,
        });
        unselectAll();
      }}
      className="relative flex items-center gap-2"
    >
      <DeleteDocumentIcon className="w-5 h-5" />
      <span>Recycle Bin</span>
    </Button>
  );
}
